import { getSaleBoughtAt } from './sales-activity'

export interface GlobalSaleCorrection {
	inscription_id: string
	block_timestamp?: number
	price_sats?: number
	marketplace?: number
	seller_address?: string | null
	buyer_address?: string | null
	txid?: string
	inscriptions_in_tx?: number
	amount?: number | string | null
}

interface CorrectionFetchOptions {
	apiUrl?: string
	headers?: HeadersInit
}

const DEFAULT_CORRECTION_API_URL = 'https://turbo.ordinalswallet.com'
const SIGNATURE_SEPARATOR = '\u001f'

const positiveFiniteNumber = (value: unknown): number => {
	const n =
		typeof value === 'number'
			? value
			: typeof value === 'string' && value.trim() !== ''
			? Number(value)
			: 0
	return Number.isFinite(n) && n > 0 ? n : 0
}

export const getSaleTimestampSeconds = (item: any): number => {
	const blockTimestamp = positiveFiniteNumber(item?.block_timestamp)
	if (blockTimestamp > 0) return Math.floor(blockTimestamp)

	const boughtAt = getSaleBoughtAt(item)
	const ts = Date.parse(boughtAt)
	return Number.isFinite(ts) ? Math.floor(ts / 1000) : 0
}

const correctionKey = (inscriptionId: string, timestampSeconds: number) =>
	`${inscriptionId}:${timestampSeconds}`

const hasCorrectedPrice = (item: any) =>
	positiveFiniteNumber(item?.price_sats) > 0 ||
	positiveFiniteNumber(item?.sale_price_sats) > 0 ||
	positiveFiniteNumber(item?.escrow?.price_sats) > 0

const correctionSignature = (item: any) => {
	const id = item?.id || item?.inscription_id
	const slug = item?.collection?.slug
	if (!id || !slug || hasCorrectedPrice(item)) return null
	return [slug, id, getSaleTimestampSeconds(item)].join(SIGNATURE_SEPARATOR)
}

export async function fetchGlobalSaleCorrectionsForItems(
	items: any[],
	options: CorrectionFetchOptions = {}
): Promise<GlobalSaleCorrection[]> {
	const apiUrl = options.apiUrl || DEFAULT_CORRECTION_API_URL
	const signatures = Array.from(
		new Set(items.map(correctionSignature).filter((v): v is string => !!v))
	)
	if (!apiUrl || signatures.length === 0) return []

	const bySlug = new Map<string, string[]>()
	for (const signature of signatures) {
		const [slug, id] = signature.split(SIGNATURE_SEPARATOR)
		if (!slug || !id) continue
		const rows = bySlug.get(slug) || []
		rows.push(id)
		bySlug.set(slug, rows)
	}

	// Compatibility for older cached rows only. New API rows carry canonical
	// price_sats and make no correction requests. Bound both time and fan-out.
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), 3000)
	const queue = Array.from(bySlug.entries())
	const pages: GlobalSaleCorrection[][] = []
	try {
		await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
			while (queue.length && !controller.signal.aborted) {
				const [slug, ids] = queue.shift()!
				const limit = Math.min(100, Math.max(20, ids.length * 2))
				const url = `${apiUrl.replace(/\/$/, '')}/collection/${encodeURIComponent(slug)}/sales?limit=${limit}`
				try {
					const res = await fetch(url, { headers: options.headers, signal: controller.signal })
					if (!res.ok) continue
					const body = await res.json()
					if (Array.isArray(body?.sales)) pages.push(body.sales)
				} catch { /* A slow collection cannot hold the entire feed open. */ }
			}
		}))
	} finally {
		clearTimeout(timer)
	}

	return pages.flat()
}

export async function correctGlobalActivityItems<T extends any[]>(
	items: T,
	options: CorrectionFetchOptions = {}
): Promise<T> {
	const corrections = await fetchGlobalSaleCorrectionsForItems(items, options)
	return mergeGlobalActivitySaleCorrections(items, corrections) as T
}

export function mergeGlobalActivitySaleCorrections<T extends any>(
	items: T[],
	corrections: GlobalSaleCorrection[]
): T[] {
	if (!items.length || !corrections.length) return items

	const exact = new Map<string, GlobalSaleCorrection>()
	const latestByInscription = new Map<string, GlobalSaleCorrection>()

	for (const row of corrections) {
		const id = row?.inscription_id
		const price = positiveFiniteNumber(row?.price_sats)
		if (!id || price <= 0) continue

		const ts = positiveFiniteNumber(row.block_timestamp)
		if (ts > 0) exact.set(correctionKey(id, Math.floor(ts)), row)

		const existing = latestByInscription.get(id)
		const existingTs = positiveFiniteNumber(existing?.block_timestamp)
		if (!existing || ts >= existingTs) latestByInscription.set(id, row)
	}

	let changed = false
	const next = items.map((item: any) => {
		const id = item?.id || item?.inscription_id
		if (!id) return item

		const ts = getSaleTimestampSeconds(item)
		const correction =
			ts > 0 ? exact.get(correctionKey(id, ts)) : latestByInscription.get(id)
		const price = positiveFiniteNumber(correction?.price_sats)
		if (!correction || price <= 0) return item

		changed = true
		const blockTimestamp = positiveFiniteNumber(correction.block_timestamp)
		return {
			...item,
			price_sats: price,
			block_timestamp: blockTimestamp || item?.block_timestamp,
			txid: correction.txid ?? item?.txid,
			marketplace: correction.marketplace ?? item?.marketplace,
			inscriptions_in_tx: correction.inscriptions_in_tx ?? item?.inscriptions_in_tx,
			amount: correction.amount ?? item?.amount,
			escrow: {
				...(item?.escrow || {}),
				satoshi_price: price,
				bought_at:
					item?.escrow?.bought_at ||
					item?.bought_at ||
					(blockTimestamp ? new Date(blockTimestamp * 1000).toISOString() : undefined),
				seller_address:
					correction.seller_address ?? item?.escrow?.seller_address ?? null,
				buyer_address: correction.buyer_address ?? item?.escrow?.buyer_address ?? null
			}
		}
	})

	return changed ? (next as T[]) : items
}
